import React, { useState, useEffect, useRef, useMemo } from "react";
import dayjs from "dayjs";
import { Calendar, dayjsLocalizer, View } from "react-big-calendar";
import { useNavigate } from "react-router-dom";
import { useApi } from "../../../service/ApiContext";
import Loader, { LocalStorage } from "../../../utils/Utils";
import Logger from "../../../utils/Logger";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import GenericModal from "../../learning_room/components/GenericModelComponent";
import StartsAt from "./../../../assets/images/calender/sand-clock-starts.png";
import EndsAt from "./../../../assets/images/calender/sand-clock-ends.png";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "../styles/minicalendar.css";
import { setPrograms } from "../../calender/data/redux/reducer/ProgramDetailReducer";
import CommonUtils from "../../../utils/Common_Utils";

const localizer = dayjsLocalizer(dayjs);

interface Event {
  event_id: string;
  event_type: string;
  event_title: string;
  event_description?: string;
  start_date: any;
  end_date: string;
  start_time?: string;
  end_time?: string;
  program_title?: string;
  program_id?: string | null;
  // Add properties for grouped events
  id?: any;
  events?: Event[]; // Array of events for grouped view
}

interface MiniCalendarProps {
  onDateSelect?: (date: string) => void;
  calendarHeight?: string;
}

// Mini Year/Month Dropdown component
const MiniYearMonthDropdown: React.FC<{
  currentDate: dayjs.Dayjs;
  onMonthYearChange: (date: dayjs.Dayjs) => void;
  availableYears: number[];
}> = ({ currentDate, onMonthYearChange, availableYears }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number>(currentDate.year());
  const [yearScrollIndex, setYearScrollIndex] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const shortMonths = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const YEARS_TO_SHOW = 5;

  const handleYearMonthSelect = (year: number, month: number) => {
    const newDate = dayjs().year(year).month(month);
    onMonthYearChange(newDate);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedYear(currentDate.year());
    const currentYearIndex = availableYears.indexOf(currentDate.year());
    if (currentYearIndex >= 0) {
      const newIndex =
        Math.floor(currentYearIndex / YEARS_TO_SHOW) * YEARS_TO_SHOW;
      setYearScrollIndex(newIndex);
    }
  }, [currentDate, availableYears]);

  const currentYear = currentDate.year();
  const currentMonth = currentDate.month();

  const getDisplayText = () => {
    const shortMonth = shortMonths[currentMonth];
    const twoDigitYear = currentYear.toString().slice(-2);
    return `${shortMonth}'${twoDigitYear}`;
  };

  const getVisibleYears = () => {
    return availableYears.slice(
      yearScrollIndex,
      yearScrollIndex + YEARS_TO_SHOW,
    );
  };

  const visibleYears = getVisibleYears();
  const hasNextYears = yearScrollIndex + YEARS_TO_SHOW < availableYears.length;
  const hasPrevYears = yearScrollIndex > 0;

  return (
    <div className="mini-year-month-dropdown-wrapper" ref={dropdownRef}>
      <button
        className="mini-year-month-dropdown-btn"
        onClick={() => setIsOpen(!isOpen)}
      >
        {getDisplayText()}
        <span className={`mini-dropdown-arrow ${isOpen ? "open" : ""}`}>▼</span>
      </button>

      {isOpen && (
        <div className="mini-year-month-dropdown-menu">
          <div className="mini-years-navigation-container">
            <button
              className={`mini-year-nav-arrow ${!hasPrevYears ? "disabled" : ""}`}
              onClick={() =>
                setYearScrollIndex(yearScrollIndex - YEARS_TO_SHOW)
              }
              disabled={!hasPrevYears}
            >
              ◀
            </button>
            <div className="mini-years-horizontal-container">
              {visibleYears.map((year) => (
                <button
                  key={year}
                  className={`mini-year-tab ${year === selectedYear ? "active" : ""}`}
                  onClick={() => setSelectedYear(year)}
                >
                  {year}
                </button>
              ))}
            </div>
            <button
              className={`mini-year-nav-arrow ${!hasNextYears ? "disabled" : ""}`}
              onClick={() =>
                setYearScrollIndex(yearScrollIndex + YEARS_TO_SHOW)
              }
              disabled={!hasNextYears}
            >
              ▶
            </button>
          </div>
          <div className="mini-months-grid-container">
            <div className="mini-months-grid">
              {months.map((month, monthIndex) => {
                const isSelected =
                  selectedYear === currentYear && monthIndex === currentMonth;
                return (
                  <button
                    key={monthIndex}
                    className={`mini-month-option ${isSelected ? "selected" : ""}`}
                    onClick={() =>
                      handleYearMonthSelect(selectedYear, monthIndex)
                    }
                  >
                    {month.slice(0, 3)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Format date with ordinal (e.g., 16 March 2026)
const formatDateWithOrdinal = (dateString: string) => {
  const date = dayjs(dateString);
  const day = date.date();
  const month = date.format("MMMM");
  const year = date.format("YYYY");

  const ordinalSuffix = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return `${ordinalSuffix(day)} ${month} ${year}`;
};

// Mini Event Card Component
const MiniEventCard: React.FC<{ event: Event }> = ({ event }) => {
  let badgeColor = "";
  let cardClass = "mini-event-card";

  switch (event.event_type) {
    case "JOZUNA_ASSESSMENT":
      cardClass += " mini-event-card-join";
      badgeColor = "#ED8627";
      break;
    case "LIVE_SESSION":
      cardClass += " mini-event-card-live";
      badgeColor = "#8924B9";
      break;
    case "PROGRAM_KICKOFF":
      cardClass += " mini-event-card-kickoff";
      badgeColor = "#09BD3C";
      break;
    case "TECHNICAL_ASSESSMENT":
      cardClass += " mini-event-card-technical";
      badgeColor = "#B99A32";
      break;
    default:
      cardClass += " mini-event-card-default";
      badgeColor = "#0A68AC";
      break;
  }

  return (
    <div className={cardClass}>
      {event.program_title && (
        <span
          className="mini-event-badge"
          style={{ backgroundColor: badgeColor, color: "white" }}
        >
          {event.program_title || ""}
        </span>
      )}
      <h3 className="mini-event-title">{event.event_title}</h3>
      {event.event_description && (
        <p className="mini-event-description">{event.event_description}</p>
      )}
      <div className="mini-starts-ends-card-wrapper">
        <div className="mini-event-time-row">
          <img src={StartsAt} alt="start" className="mini-event-time-icon" />
          <span className="mini-event-time-label">Starts at:</span>
          <span className="mini-event-time-value">
            {dayjs(event.start_date).format("DD.MM.YYYY")} |{" "}
            {event.start_time || "09:00 AM"}
          </span>
        </div>
        <div className="mini-event-time-row">
          <img src={EndsAt} alt="end" className="mini-event-time-icon" />
          <span className="mini-event-time-label">Ends by:</span>
          <span className="mini-event-time-value">
            {dayjs(event.end_date).format("DD.MM.YYYY")} |{" "}
            {event.end_time || "06:00 PM"}
          </span>
        </div>
      </div>
    </div>
  );
};

const MiniCalendar: React.FC<MiniCalendarProps> = ({
  onDateSelect,
  calendarHeight = "210px",
}) => {
  const navigate = useNavigate();
  const api = useApi();

  const [currentDate, setCurrentDate] = useState(dayjs());
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [view, setView] = useState<View>("month");
  const [selectedDateEvents, setSelectedDateEvents] = useState<Event[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDateTitle, setSelectedDateTitle] = useState("");

  // State for available years
  const [availableYears, setAvailableYears] = useState<number[]>(() => {
    const currentYear = dayjs().year();
    return Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  });

  const listProgramData = useSelector(
    (state: RootState) => state.programs.programs,
  );
  const dispatch = useDispatch();

  const getProgram = async () => {
    Loader.showLoading("Loading...");
    api.setAuthToken(LocalStorage.getLocalData("access_token"));
    const institute_id = LocalStorage.getLocalData("institute_id");
    try {
      const response = await api.get<any>(`/eprogram/${institute_id}`);
      if (response.data.error) {
        dispatch(setPrograms([]));
        return;
      }
      if (response.data.programs && response.data.programs.length > 0) {
        Logger.info("response data", response.data.programs);
        dispatch(setPrograms(response.data.programs));
      } else {
        dispatch(setPrograms([]));
      }
    } catch (error) {
      Logger.info("error response", error);
    } finally {
      Loader.dismissLoading();
    }
  };

  // Function to fetch both program and non-program events
  const getBothProgramEvents = async (selectedDate: dayjs.Dayjs) => {
    setIsLoading(true);
    api.setAuthToken(LocalStorage.getLocalData("access_token"));

    const month = selectedDate.format("MM");
    const year = selectedDate.format("YYYY");

    const url = `/non-program/events?month=${month}&year=${year}&offset_time=${
      CommonUtils.findOffsetDuration().offset
    }&is_dashboard=true`;

    try {
      const response = await api.get<any>(url);

      let allEvents: Event[] = [];

      // Get non-program events
      if (response.data && response.data.non_program) {
        const nonProgramEvents = response.data.non_program.events || [];
        allEvents = [...allEvents, ...nonProgramEvents];
      }

      // Get program-related events
      if (response.data && response.data.program_related) {
        const programEvents = response.data.program_related.events || [];
        allEvents = [...allEvents, ...programEvents];
      }

      setEvents(allEvents);

      // Update available years from both sources
      const yearsSet = new Set<number>();

      // Add years from non-program events
      if (response.data.non_program?.events) {
        response.data.non_program.events.forEach((event: Event) => {
          if (event.start_date) {
            yearsSet.add(dayjs(event.start_date).year());
          }
          if (event.end_date) {
            yearsSet.add(dayjs(event.end_date).year());
          }
        });
      }

      // Add years from program-related events
      if (response.data.program_related?.events) {
        response.data.program_related.events.forEach((event: Event) => {
          if (event.start_date) {
            yearsSet.add(dayjs(event.start_date).year());
          }
          if (event.end_date) {
            yearsSet.add(dayjs(event.end_date).year());
          }
        });
      }

      // Also add years from program_related date range
      if (response.data.program_related?.start_date) {
        yearsSet.add(dayjs(response.data.program_related.start_date).year());
      }
      if (response.data.program_related?.end_date) {
        yearsSet.add(dayjs(response.data.program_related.end_date).year());
      }

      // Convert set to sorted array
      const sortedYears = Array.from(yearsSet).sort((a, b) => a - b);

      // If no years found, use default range (current year ± 5)
      if (sortedYears.length === 0) {
        const currentYear = dayjs().year();
        const defaultYears = Array.from(
          { length: 10 },
          (_, i) => currentYear - 5 + i,
        );
        setAvailableYears(defaultYears);
      } else {
        // Ensure we have a reasonable range (at least 10 years)
        const minYear = Math.min(...sortedYears);
        const maxYear = Math.max(...sortedYears);
        const yearRange = maxYear - minYear;

        if (yearRange < 10) {
          // Expand range to have at least 10 years
          const padding = Math.floor((10 - yearRange) / 2);
          const startYear = minYear - padding;
          const endYear = maxYear + (10 - yearRange - padding);
          const expandedYears = Array.from(
            { length: endYear - startYear + 1 },
            (_, i) => startYear + i,
          );
          setAvailableYears(expandedYears);
        } else {
          setAvailableYears(sortedYears);
        }
      }

      Logger.info(`Total events: ${allEvents.length}`);
      Logger.info(`Available years: ${availableYears}`);
    } catch (error) {
      Logger.info("Error fetching events", error);
      setEvents([]);
    } finally {
      setIsLoading(false);
      Loader.dismissLoading();
    }
  };

  // Expand multi-day events
  const expandMultiDayEvents = (events: Event[]): Event[] => {
    const expandedEvents: Event[] = [];
    events.forEach((event) => {
      const startDate = dayjs(event.start_date);
      const endDate = dayjs(event.end_date);
      const daysDiff = endDate.diff(startDate, "day");

      if (daysDiff === 0) {
        expandedEvents.push(event);
      } else {
        for (let i = 0; i <= daysDiff; i++) {
          const currentDate = startDate.add(i, "day");
          expandedEvents.push({
            ...event,
            event_id: `${event.event_id}_${i}`,
            start_date: currentDate.format("YYYY/MM/DD"),
            end_date: currentDate.format("YYYY/MM/DD"),
          });
        }
      }
    });
    return expandedEvents;
  };

  const expandedEvents = useMemo(() => {
    const expanded = expandMultiDayEvents(events);

    // Group events by date
    const groupedMap = new Map();
    expanded.forEach((event) => {
      const dateKey = dayjs(event.start_date).format("YYYY-MM-DD");
      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, []);
      }
      groupedMap.get(dateKey).push(event);
    });

    // Convert to array of grouped events
    const groupedEvents: Event[] = [];
    groupedMap.forEach((eventsList, date) => {
      groupedEvents.push({
        id: date,
        event_id: `grouped_${date}`,
        start_date: date,
        end_date: date,
        events: eventsList,
        event_type: "GROUPED",
        event_title: `${eventsList.length} events`,
        program_title: "",
      });
    });

    return groupedEvents;
  }, [events]);

  // Get events for a specific date
  const getEventsForDate = (date: Date) => {
    const formattedDate = dayjs(date).format("YYYY/MM/DD");
    return events.filter((event) => {
      const eventStart = dayjs(event.start_date);
      const eventEnd = dayjs(event.end_date);
      const clickedDate = dayjs(formattedDate);
      return clickedDate.isBetween(eventStart, eventEnd, "day", "[]");
    });
  };

  // Custom Event Dot Component that shows multiple dots
  const EventDot: React.FC<{ event: any }> = ({ event }) => {
    const calendarCurrentDate = currentDate;
    const eventsForDay = event.events || [event];

    const currentMonthEvents = eventsForDay.filter((e: Event) => {
      const eventDate = dayjs(e.start_date);
      return (
        eventDate.month() === calendarCurrentDate.month() &&
        eventDate.year() === calendarCurrentDate.year()
      );
    });

    if (currentMonthEvents.length === 0) {
      return null;
    }

    const MAX_DOTS = 2;
    const visibleEvents = currentMonthEvents.slice(0, MAX_DOTS);
    const remainingCount = currentMonthEvents.length - MAX_DOTS;

    return (
      <div className="mini-event-dots-container">
        {visibleEvents.map((e: Event, idx: number) => {
          let dotColor = "#0A68AC";
          switch (e.event_type) {
            case "JOZUNA_ASSESSMENT":
              dotColor = "#ED8627";
              break;
            case "LIVE_SESSION":
              dotColor = "#8924B9";
              break;
            case "PROGRAM_KICKOFF":
              dotColor = "#09BD3C";
              break;
            case "TECHNICAL_ASSESSMENT":
              dotColor = "#B99A32";
              break;
          }
          return (
            <div
              key={`${e.event_id}_${idx}`}
              className="mini-event-dot"
              style={{ backgroundColor: dotColor }}
            />
          );
        })}
        {remainingCount > 0 && (
          <span className="mini-more-dots">+{remainingCount} more</span>
        )}
      </div>
    );
  };

  // Custom Header Component
  const CustomHeader = ({ label }: { label: string }) => {
    const dayNames: { [key: string]: string } = {
      Sun: "S",
      Mon: "M",
      Tue: "T",
      Wed: "W",
      Thu: "TH",
      Fri: "F",
      Sat: "SA",
    };
    return (
      <div style={{ textAlign: "center" }}>
        <span className="mini-calendar-weekday-header">
          {dayNames[label] || label}
        </span>
      </div>
    );
  };

  const handleMonthYearChange = (newDate: dayjs.Dayjs) => {
    setCurrentDate(newDate);
    // Fetch events for the new month/year
    getBothProgramEvents(newDate);
  };

  const handleSelectEvent = (event: Event, e: React.SyntheticEvent) => {
    const eventDate = dayjs(event.start_date).toDate();
    const eventsForDate = getEventsForDate(eventDate);
    setSelectedDateEvents(eventsForDate);
    setSelectedDateTitle(formatDateWithOrdinal(event.start_date));
    setIsModalOpen(true);

    if (onDateSelect) {
      onDateSelect(dayjs(eventDate).format("YYYY-MM-DD"));
    }
  };

  const dayPropGetter = (date: Date) => {
    const isToday = dayjs(date).isSame(dayjs(), "day");
    const eventsForDate = getEventsForDate(date);
    const hasEvents = eventsForDate.length > 0;

    return {
      className: `mini-calendar-day-cell ${isToday ? "mini-today-highlight" : ""} ${hasEvents ? "has-events" : ""}`,
      style: {
        cursor: hasEvents ? "pointer" : "default",
      },
    };
  };

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const init = async () => {
      // await getProgram(); // fetch programs
      await getBothProgramEvents(currentDate); // then events
    };
    init();
  }, []);
  return (
    <div className="mini-calendar-container">
      <div className="mini-calendar-header">
        <div className="mini-calendar-title">
          <span>Learning Schedule</span>
        </div>
        <div
          className="mini-calendar-view-link"
          onClick={() => navigate("/dashboard/programSchedule")}
        >
          View
        </div>
      </div>

      <div className="mini-calendar-controls">
        <MiniYearMonthDropdown
          currentDate={currentDate}
          onMonthYearChange={handleMonthYearChange}
          availableYears={availableYears}
        />
      </div>

      <Calendar
        localizer={localizer}
        date={currentDate.toDate()}
        events={expandedEvents}
        startAccessor={(event: Event) => new Date(event.start_date)}
        endAccessor={(event: Event) => new Date(event.end_date)}
        style={{ height: calendarHeight }}
        components={{
          header: CustomHeader,
          event: EventDot,
          toolbar: () => null,
        }}
        dayPropGetter={dayPropGetter}
        view={view}
        onSelectEvent={handleSelectEvent}
        formats={{
          dateFormat: (date) => date.getDate().toString(),
          dayFormat: "d",
        }}
      />

      <GenericModal
        isOpen={isModalOpen}
        title={selectedDateTitle}
        onClose={() => setIsModalOpen(false)}
      >
        <div className="mini-events-list">
          {selectedDateEvents.map((event, index) => (
            <MiniEventCard key={`${event.event_id}_${index}`} event={event} />
          ))}
        </div>
      </GenericModal>
    </div>
  );
};

export default MiniCalendar;
